import React, { useEffect } from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useUserHook from 'hooks/useUserHook';

import { useMutationCustomerSet } from 'api/mutations/customerSet.mutation';
import { useQueryCustomerGet } from 'api/queries/customerGet.query';

import { WidgetTitle } from 'components/common/WidgetTitle/WidgetTitle';
import { WidgetWithTitle } from 'components/common/WidgetWithTitle/WidgetWithTitle';

import { Button } from 'components/DesignSystem/Button';
import classes from 'components/widgets/CustomerProfile/CustomerProfile.module.scss';
import { AiType } from 'components/widgets/CustomerProfile/components/AiType/AiType';
import { ConfettiOn } from 'components/widgets/CustomerProfile/components/ConfettiOn/ConfettiOn';
import { CustomerName } from 'components/widgets/CustomerProfile/components/CustomerName/CustomerName';
import { MaxUsers } from 'components/widgets/CustomerProfile/components/MaxUsers/MaxUsers';
import { MaxWbsLevel } from 'components/widgets/CustomerProfile/components/MaxWbsLevel/MaxWbsLevel';
import { OpcHost } from 'components/widgets/CustomerProfile/components/OpcHost/OpcHost';
import { OpcPassword } from 'components/widgets/CustomerProfile/components/OpcPassword/OpcPassword';
import { OpcUsername } from 'components/widgets/CustomerProfile/components/OpcUsername/OpcUsername';

export interface FormProps {
    customerName: string;
    opcHost: string;
    opcUsername: string;
    opcPassword: string;
    maxUsers: number;
    maxWbsLevel: number;
    confettiOn: boolean;
    aiType: string;
}

export const CustomerProfile = () => {
    const { customerId } = useUserHook();
    const { data } = useQueryCustomerGet({ customerId });
    const { mutate } = useMutationCustomerSet();
    const { handleSnackBar } = useCustomSnackBar();
    const form = useForm<FormProps>();

    useEffect(() => {
        if (data) {
            form.reset({
                customerName: data.description,
                opcHost: data.opcHost,
                opcUsername: data.opcUsername,
                opcPassword: data.opcPassword,
                maxUsers: data.maxUsers,
                maxWbsLevel: data.defaultMaxWbsLevel,
                confettiOn: Boolean(data.confetti),
                aiType: data.aiType,
            });
        }
    }, [data, form]);

    const handleFormSubmit = (data: FormProps) => {
        if (customerId) {
            mutate(
                {
                    id: customerId,
                    description: data.customerName,
                    opcHost: data.opcHost,
                    opcUsername: data.opcUsername,
                    opcPassword: data.opcPassword,
                    maxUsers: data.maxUsers,
                    maxWbsLevel: data.maxWbsLevel,
                    confettiON: data.confettiOn,
                    aiType: data.aiType,
                    name: data.customerName,
                },
                {
                    onSuccess: () => {
                        handleSnackBar('Successfully updated!', 'success');
                    },
                    onError: () => {
                        handleSnackBar('Failed to update customer profile.', 'error');
                    },
                },
            );
        }
    };

    return (
        <WidgetWithTitle title={<WidgetTitle title={'Customer Profile'} />}>
            <FormProvider {...form}>
                <form onSubmit={form.handleSubmit(handleFormSubmit)} className={classes.form}>
                    <CustomerName />
                    <OpcHost />
                    <OpcUsername />
                    <OpcPassword />
                    <MaxUsers />
                    <MaxWbsLevel />
                    <ConfettiOn />
                    <AiType />
                    <div className={classes.actions}>
                        <Button size={'large'} type={'submit'} variant={'primary'}>
                            Save
                        </Button>
                    </div>
                </form>
            </FormProvider>
        </WidgetWithTitle>
    );
};
